import RemoveCustomer from "@builder/components/app/ActionBuilder/ActionTab/Function/Customer/RemoveCustomer";
import OliverText from "@builder/components/general/OliverText";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { ActionBuilderType, getSubtitleByType, getTitleByType } from "@builder/utils/actionBuilderHelpers";
import { useTranslation } from "react-i18next";
import Condition from "./Condition";
import Confirmation from "./Confirmation";
import Function from "./Function";
import AddCartDiscount from "./Function/Cart/AddCartDiscount";
import AddCartFee from "./Function/Cart/AddCartFee";
import AddCartNote from "./Function/Cart/AddCartNote";
import AddCustomerNote from "./Function/Customer/AddCustomerNode";
import AddProduct from "./Function/General/AddProduct";
import AuthenticateUser from "./Function/General/AuthenticateUser";
import ClearCart from "./Function/General/ClearCart";
import SlideOut from "./Function/General/SlideOut";
import SplitPayment from "./Function/General/SplitPayment";
import UpdateCFD from "./Function/General/UpdateCFD";
import AddProductDiscount from "./Function/Product/AddProductDiscount";
import AddProductFee from "./Function/Product/AddProductFee";
import AddProductNote from "./Function/Product/AddProductNote";
import AdjustInventory from "./Function/Product/AdjustInventory";
import ShopifyOrderStatus from "./Function/ThirdParty/ShopifyOrderStatus";
import WooOrderStatus from "./Function/ThirdParty/WooOrderStatus";
import Notification from "./Notification";
import Page from "./Page";
import OpenCashDrawer from "./Function/General/OpenCashDrawer";
import StationHome from "./Function/General/StationHome";
import Popup from "./Popup";
import Start from "./Start";
import { removeBuilderAction } from "@builder/store/slices/actionBuilderSlice";
import CreateRefund from "./Function/Cart/CreateRefund";
import SetProductAsActive from "./Function/Product/SetProductAsActive";
import CreateCustomer from "./Function/Customer/CreateNewCustomer";
import EditCustomer from "./Function/Customer/EditCustomer";
import AddCustomerToCart from "./Function/Customer/AddCustomerToCart";
import CashPaymentAction from "./Function/Cart/CashPayment";
import { useEffect } from "react";
import TapToPayAction from "./Function/Cart/TapToPay";
import TerminalAction from "./Function/Cart/Terminal";
import MotoAction from "./Function/Cart/Moto";
import CustomPaymentAction from "./Function/Cart/CustomPayment";
import ParkOrder from "./Function/Cart/ParkOrder";
import ViewParkOrder from "./Function/Cart/ViewParkedOrder";
import ResumeParkOrder from "./Function/Cart/ResumeParkOrder";
import DeleteParkOrder from "./Function/Cart/DeleteParkOrder";
import SwitchUser from "./Function/General/SwitchUser";
import Webhook from "./Function/ThirdParty/Webhook";
import ReOrder from "./Function/General/ReOrder";
import CustomSale from "./Function/Cart/CustomSale";
import Zapier from "./Function/ThirdParty/Zapier";
import Print from "./Function/General/Print";

const ActionTab = () => {
    const { tab, id } = useAppSelector(state => state.actionBuilder.current);
    const { actionsEditor } = useAppSelector(state => state.actionBuilder);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (tab && !actionsEditor.map(i => i.id).includes(id!)) {
            dispatch(removeBuilderAction(id!));
        }
    }, [tab, id, actionsEditor, dispatch]);

    if (!tab) return;

    const renderTab = (tab: ActionBuilderType | null | undefined) => {
        switch (tab) {
            case ActionBuilderType.START:
                return <Start />;
            case ActionBuilderType.NEW:
                return <Function />;
            case ActionBuilderType.CONDITION:
                return <Condition key={id} selectedTab={id} />;
            case ActionBuilderType.PAGE:
                return <Page />;
            case ActionBuilderType.OPEN_CASH_DRAWER:
                return <OpenCashDrawer />;
            case ActionBuilderType.STATION_HOME:
                return <StationHome />;
            case ActionBuilderType.POPUP:
                return <Popup />;
            case ActionBuilderType.NOTIFICATION:
                return <Notification />;
            case ActionBuilderType.CONFIRMATION:
                return <Confirmation />;
            case ActionBuilderType.ADD_CART_NOTE:
                return <AddCartNote />;
            case ActionBuilderType.ADD_CART_DISCOUNT:
                return <AddCartDiscount />;
            case ActionBuilderType.ADD_CART_FEE:
                return <AddCartFee />;
            case ActionBuilderType.ADD_PRODUCT_DISCOUNT:
                return <AddProductDiscount />;
            case ActionBuilderType.ADD_PRODUCT_NOTE:
                return <AddProductNote />;
            case ActionBuilderType.ADD_PRODUCT_FEE:
                return <AddProductFee />;
            case ActionBuilderType.ADD_CUSTOMER_NOTE:
                return <AddCustomerNote />;
            case ActionBuilderType.SPLIT_PAYMENT:
                return <SplitPayment />;
            case ActionBuilderType.ADJUST_INVENTORY:
                return <AdjustInventory />;
            case ActionBuilderType.WOO_ORDER_STATUS:
                return <WooOrderStatus />;
            case ActionBuilderType.SHOPIFY_ORDER_STATUS:
                return <ShopifyOrderStatus />;
            case ActionBuilderType.ADD_PRODUCT_TO_CART:
                return <AddProduct />;
            case ActionBuilderType.CLEAR_CART:
                return <ClearCart />;
            case ActionBuilderType.SLIDE_OUT:
                return <SlideOut />;
            case ActionBuilderType.AUTHENTICATE_USER:
                return <AuthenticateUser />;
            case ActionBuilderType.CFD_PAGE:
                return <UpdateCFD />;
            case ActionBuilderType.PRINT:
                return <Print />;
            case ActionBuilderType.CLEAR_CUSTOMER:
                return <RemoveCustomer />;
            case ActionBuilderType.REFUND:
                return <CreateRefund />;
            case ActionBuilderType.SET_PRODUCT_ACTIVE:
                return <SetProductAsActive />;
            case ActionBuilderType.CREATE_CUSTOMER:
                return <CreateCustomer />;
            case ActionBuilderType.EDIT_CUSTOMER:
                return <EditCustomer />;
            case ActionBuilderType.ADD_CUSTOMER:
                return <AddCustomerToCart />;
            case ActionBuilderType.CASH_PAYMENT:
                return <CashPaymentAction />;
            case ActionBuilderType.TAP_TO_PAY:
                return <TapToPayAction />;
            case ActionBuilderType.TERMINAL:
                return <TerminalAction />;
            case ActionBuilderType.MOTO:
                return <MotoAction />;
            case ActionBuilderType.CUSTOM_PAYMENT:
                return <CustomPaymentAction />;
            case ActionBuilderType.PARK_ORDER:
                return <ParkOrder />;
            case ActionBuilderType.VIEW_PARK_ORDER:
                return <ViewParkOrder />;
            case ActionBuilderType.RESUME_PARK_ORDER:
                return <ResumeParkOrder />;
            case ActionBuilderType.DELETE_PARK_ORDER:
                return <DeleteParkOrder />;
            case ActionBuilderType.SWITCH_USER:
                return <SwitchUser />;
            case ActionBuilderType.WEBHOOK:
                return <Webhook />;
            case ActionBuilderType.ZAPIER:
                return <Zapier />;
            case ActionBuilderType.RE_ORDER:
                return <ReOrder />;
            case ActionBuilderType.ADD_CUSTOM_SALE:
                return <CustomSale />;
            default:
                return null;
        }
    };

    return (
        <>
            <div className="action-menu-title-wrapper">
                <div className="action-menu-title">
                    <OliverText text={t("popup.actionBuilder." + getTitleByType(tab))} size="s" weight="medium-heavy" />
                </div>
                {getSubtitleByType(tab) ? <OliverText text={t("popup.actionBuilder." + getSubtitleByType(tab))} size="xs" weight="light" /> : null}
            </div>
            {renderTab(tab)}
        </>
    );
};

export default ActionTab;
